import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import {
  ShipmentDetailsApiRow,
  ShipmentProductApiRow,
} from "../../../../../models/Shipments/ShipmentDetails";
import ShipmentsAPI from "../../../api/ShipmentsAPI";
import AddShipmentLinesSection from "./AddShipmentLinesSection";
import {
  getModernPageColors,
  modernEmptyStateSx,
  modernSectionPaperSx,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTableRowSx,
} from "../../../../../styles/modernListingPageSx";

type PendingRemove = {
  product: ShipmentProductApiRow;
  rowKey: string;
  mode: "pack" | "item";
};

interface ShipmentProductsSectionProps {
  shipmentId: number;
  products: ShipmentProductApiRow[] | null | undefined;
  inventoryId: number | null | undefined;
  canAdd: boolean;
  canRemove: boolean;
  shipmentsAPI: ShipmentsAPI;
  headingVariant?: "heading6" | "heading7";
  onProductsChange: (products: ShipmentProductApiRow[]) => void;
}

const getProductRowKey = (product: ShipmentProductApiRow, index: number) =>
  `${product.line_id ?? "nl"}-${product.swagpack_id ?? "np"}-${product.product_id}-${product.sku ?? "na"}-${index}`;

const ProductsTable: React.FC<{
  products: ShipmentProductApiRow[];
  canRemove: boolean;
  pendingRowKey: string | null;
  removing: boolean;
  onRequestRemove: (product: ShipmentProductApiRow, rowKey: string) => void;
}> = ({ products, canRemove, pendingRowKey, removing, onRequestRemove }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const stickySkuHeaderSx = {
    position: "sticky" as const,
    left: 0,
    zIndex: 3,
    minWidth: 260,
    width: 260,
    backgroundColor: colors.headerBg,
    boxShadow: `2px 0 6px ${theme.palette.customColors.shadowLightBorder}`,
  };
  const stickySkuBodySx = {
    position: "sticky" as const,
    left: 0,
    zIndex: 2,
    minWidth: 260,
    width: 260,
    backgroundColor: colors.surface,
    boxShadow: `2px 0 6px ${theme.palette.customColors.shadowLightBorder}`,
  };
  const stickyActionHeaderSx = {
    position: "sticky" as const,
    right: 0,
    zIndex: 3,
    minWidth: 72,
    width: 72,
    backgroundColor: colors.headerBg,
    boxShadow: `-2px 0 6px ${theme.palette.customColors.shadowLightBorder}`,
  };
  const stickyActionBodySx = {
    position: "sticky" as const,
    right: 0,
    zIndex: 2,
    minWidth: 72,
    width: 72,
    backgroundColor: colors.surface,
    boxShadow: `-2px 0 6px ${theme.palette.customColors.shadowLightBorder}`,
  };

  if (!products.length) {
    return (
      <Box
        sx={{
          ...modernEmptyStateSx(theme),
          py: 4,
          border: `1px dashed ${colors.border}`,
          borderRadius: 2,
        }}
      >
        <CustomTypography variant="body2">
          No products found for this shipment.
        </CustomTypography>
      </Box>
    );
  }

  const columnMinWidths: Record<string, number> = {
    Pack: 200,
    Product: 240,
    SKU: 260,
  };

  const headers = [
    "SKU",
    "Quantity",
    "Product ID",
    "Product",
    "Pack",
    ...(canRemove ? ["Action"] : []),
  ];

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        ...modernSectionPaperSx(theme),
        p: 0,
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Table size="small" sx={{ minWidth: canRemove ? 840 : 760 }}>
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell
                key={header}
                align={
                  header === "Quantity" || header === "Action"
                    ? "center"
                    : "left"
                }
                sx={{
                  ...modernTableHeaderCellSx(theme),
                  ...(columnMinWidths[header]
                    ? { minWidth: columnMinWidths[header] }
                    : {}),
                  ...(header === "SKU" ? stickySkuHeaderSx : {}),
                  ...(header === "Action" ? stickyActionHeaderSx : {}),
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((product, index) => {
            const rowKey = getProductRowKey(product, index);
            const isPending = pendingRowKey === rowKey;

            return (
              <TableRow
                key={rowKey}
                hover
                sx={{
                  ...modernTableRowSx(theme),
                  ...(isPending
                    ? { bgcolor: `${theme.palette.warning.main}14` }
                    : {}),
                  "& td:first-of-type": {
                    backgroundColor: isPending
                      ? theme.palette.warning.light
                      : colors.surface,
                  },
                  "&:hover td:first-of-type": {
                    backgroundColor: isPending
                      ? theme.palette.warning.light
                      : colors.surfaceSoft,
                  },
                  ...(canRemove
                    ? {
                        "& td:last-of-type": {
                          backgroundColor: isPending
                            ? theme.palette.warning.light
                            : colors.surface,
                        },
                        "&:hover td:last-of-type": {
                          backgroundColor: isPending
                            ? theme.palette.warning.light
                            : colors.surfaceSoft,
                        },
                      }
                    : {}),
                }}
              >
                <TableCell
                  align="left"
                  sx={{
                    ...modernTableBodyCellSx(theme),
                    ...stickySkuBodySx,
                  }}
                >
                  <SkuCopyRow
                    sku={product.sku}
                    emptyFallback="—"
                    variant="body3"
                    fontWeight={500}
                    sx={modernTableBodyCellSx(theme)}
                  />
                </TableCell>
                <TableCell align="center" sx={modernTableBodyCellSx(theme)}>
                  {product.quantity}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ ...modernTableBodyCellSx(theme), minWidth: 90 }}
                >
                  <CustomTypography
                    variant="body3"
                    color={theme.palette.customColors.blackText}
                  >
                    {product.product_id}
                  </CustomTypography>
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    ...modernTableBodyCellSx(theme),
                    minWidth: 240,
                    width: "26%",
                  }}
                >
                  <SkuCopyRow
                    sku={product.product_name}
                    emptyFallback="—"
                    copyLabel="Copy product name"
                    fontWeight={500}
                    variant="body3"
                    color={theme.palette.customColors.blackText}
                  />
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    ...modernTableBodyCellSx(theme),
                    minWidth: 200,
                    width: "22%",
                  }}
                >
                  {product.swagpack_id != null ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      gap={0.25}
                      minWidth={0}
                    >
                      <CustomTypography
                        variant="body3"
                        fontWeight={600}
                        color={theme.palette.customColors.blackText}
                      >
                        {product.pack_label?.trim() || "—"}
                      </CustomTypography>
                      <CustomTypography
                        variant="caption"
                        color={theme.palette.customColors.greyTextColor}
                      >
                        #{product.swagpack_id}
                      </CustomTypography>
                    </Box>
                  ) : (
                    <CustomTypography
                      variant="body3"
                      color={theme.palette.customColors.greyTextColor}
                    >
                      —
                    </CustomTypography>
                  )}
                </TableCell>
                {canRemove && (
                  <TableCell
                    align="center"
                    sx={{
                      ...modernTableBodyCellSx(theme),
                      ...stickyActionBodySx,
                      ...(isPending
                        ? { backgroundColor: theme.palette.warning.light }
                        : {}),
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label="Remove from shipment"
                      disabled={removing}
                      onClick={() => onRequestRemove(product, rowKey)}
                      sx={{
                        color: isPending
                          ? theme.palette.warning.dark
                          : theme.palette.error.main,
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const RemoveConfirmBanner: React.FC<{
  pending: PendingRemove;
  removing: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ pending, removing, error, onCancel, onConfirm }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const { product, mode } = pending;

  const message =
    mode === "pack"
      ? `“${product.product_name || product.sku || "This item"}” belongs to pack #${product.swagpack_id}. Removing it will remove the entire pack from this shipment.`
      : `Remove “${product.product_name || product.sku || "this item"}” from this shipment?`;

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.warning.light}`,
        bgcolor: `${theme.palette.warning.main}0F`,
      }}
    >
      <CustomTypography
        variant="body2"
        sx={{ color: colors.title, mb: error ? 1 : 1.25, lineHeight: 1.45 }}
      >
        {message}
      </CustomTypography>
      {error && (
        <CustomTypography
          variant="body2"
          sx={{ color: theme.palette.error.main, mb: 1.25 }}
        >
          {error}
        </CustomTypography>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <AppButton
          label="Keep"
          variant="secondary"
          disabled={removing}
          onClick={onCancel}
          customStyles={{ minHeight: 32, px: 1.5 }}
          fontSize="0.75rem"
        />
        <AppButton
          label={
            removing
              ? "Removing…"
              : mode === "pack"
                ? "Remove pack"
                : "Remove item"
          }
          variant="primary"
          disabled={removing}
          onClick={onConfirm}
          customStyles={{
            minHeight: 32,
            px: 1.5,
            bgcolor: theme.palette.error.main,
            "&:hover": { bgcolor: theme.palette.error.dark },
          }}
          fontSize="0.75rem"
        />
      </Box>
    </Box>
  );
};

const ShipmentProductsSection: React.FC<ShipmentProductsSectionProps> = ({
  shipmentId,
  products,
  inventoryId,
  canAdd,
  canRemove,
  shipmentsAPI,
  headingVariant = "heading7",
  onProductsChange,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const productRows = products ?? [];

  const [pendingRemove, setPendingRemove] = useState<PendingRemove | null>(
    null,
  );
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  const addSectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setPendingRemove(null);
    setRemoveError(null);
    setRemoving(false);
    setIsAddSectionOpen(false);
  }, [shipmentId]);

  useEffect(() => {
    if (!isAddSectionOpen) {
      return;
    }
    try {
      addSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    } catch {
      // Section still opens even if scroll is unavailable.
    }
  }, [isAddSectionOpen]);

  const refreshProducts = async () => {
    const response = await shipmentsAPI.getShipmentDetails(shipmentId);
    if (!response?.success) {
      throw new Error(
        response?.message || "Failed to refresh shipment products.",
      );
    }
    const row = response.result as ShipmentDetailsApiRow;
    onProductsChange(Array.isArray(row.products) ? row.products : []);
  };

  const resolveItemLineId = async (
    product: ShipmentProductApiRow,
  ): Promise<number> => {
    try {
      if (product.line_id != null) {
        return product.line_id;
      }

      if (!product.sku) {
        throw new Error("SKU is missing for this item. Cannot remove.");
      }

      const linesResult = await shipmentsAPI.listShipmentLines(shipmentId);
      const match = (linesResult?.lines || []).find(
        (line) =>
          line.status !== "REMOVED" &&
          line.lineType === "ITEM" &&
          line.clientSku === product.sku,
      );

      if (!match?.lineId) {
        throw new Error(
          "Could not find a removable line for this item. Please refresh and try again.",
        );
      }

      return match.lineId;
    } catch (error) {
      throw error;
    }
  };

  const handleRequestRemove = (
    product: ShipmentProductApiRow,
    rowKey: string,
  ) => {
    try {
      setRemoveError(null);
      setPendingRemove({
        product,
        rowKey,
        mode: product.swagpack_id != null ? "pack" : "item",
      });
    } catch {
      setRemoveError("Failed to start product removal.");
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemove) {
      return;
    }

    setRemoving(true);
    setRemoveError(null);

    try {
      if (pendingRemove.mode === "pack") {
        const packId = pendingRemove.product.swagpack_id;
        if (packId == null) {
          throw new Error("Pack id is missing for this product.");
        }
        await shipmentsAPI.removeShipmentPackLines(shipmentId, packId);
      } else {
        const lineId = await resolveItemLineId(pendingRemove.product);
        await shipmentsAPI.removeShipmentLine(shipmentId, lineId);
      }

      setPendingRemove(null);
      await refreshProducts();
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setRemoveError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to remove from shipment. Please try again.",
      );
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <CustomTypography
          variant={headingVariant}
          sx={{
            color: colors.title,
            ...(headingVariant === "heading6" ? { fontWeight: 600 } : {}),
          }}
        >
          Products ({productRows.length})
        </CustomTypography>
        {canAdd && !isAddSectionOpen && (
          <AppButton
            label="Add items"
            variant="secondary"
            onClick={() => {
              setPendingRemove(null);
              setRemoveError(null);
              setIsAddSectionOpen(true);
            }}
            startIcon={<AddIcon />}
            customStyles={{ minHeight: 32, px: 1.5 }}
            fontSize="0.75rem"
          />
        )}
      </Box>
      <ProductsTable
        products={productRows}
        canRemove={canRemove}
        pendingRowKey={pendingRemove?.rowKey ?? null}
        removing={removing}
        onRequestRemove={handleRequestRemove}
      />
      {pendingRemove && canRemove && !isAddSectionOpen && (
        <RemoveConfirmBanner
          pending={pendingRemove}
          removing={removing}
          error={removeError}
          onCancel={() => {
            setPendingRemove(null);
            setRemoveError(null);
          }}
          onConfirm={() => {
            void handleConfirmRemove();
          }}
        />
      )}
      {isAddSectionOpen ? (
        <Box ref={addSectionRef}>
          <AddShipmentLinesSection
            open={isAddSectionOpen}
            shipmentId={shipmentId}
            inventoryId={inventoryId}
            shipmentsAPI={shipmentsAPI}
            onClose={() => setIsAddSectionOpen(false)}
            onSaved={async () => {
              await refreshProducts();
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default ShipmentProductsSection;
